// frontend/src/components/Modals/OrderConfirmModal.jsx

import React, { useState, useEffect } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Alert, Button, Row, Col } from 'reactstrap';
import { ordersAPI } from '../../services/api';

const OrderConfirmModal = ({ isOpen, toggle, data, quoteMatch, onConfirm }) => {
  const [loading, setLoading] = useState(false);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [orderNumber, setOrderNumber] = useState('');
  const [orderTotal, setOrderTotal] = useState('');
  const [currentQuoteMatch, setCurrentQuoteMatch] = useState(null);
  const [searchingQuotes, setSearchingQuotes] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);

  useEffect(() => {
    if (quoteMatch?.recommended) {
      setSelectedQuote({
        id: quoteMatch.recommended.id,
        match_type: quoteMatch.match_type,
      });
    } else {
      setSelectedQuote(null);
    }
  }, [quoteMatch]);

  useEffect(() => {
    if (data?.order_number) {
      setOrderNumber(data.order_number);
    }
    if (data?.total) {
      setOrderTotal(String(data.total));
    }
  }, [data]);

  // ✅ CORREGIDO: Generar URL del PDF temporal
// ✅ CORREGIDO: Generar URL del PDF usando endpoint seguro
  useEffect(() => {
    if (data?.temp_files?.pdf_path) {
      const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
    
      // Obtener la ruta del PDF
      let pdfPath = data.temp_files.pdf_path;
    
      // Normalizar separadores (Windows usa \, convertir a /)
      pdfPath = pdfPath.replace(/\\/g, '/');
    
      // Extraer solo el nombre del archivo (última parte de la ruta)
      const filename = pdfPath.split('/').pop();
    
      // Usar el endpoint seguro
      const url = `${baseURL}/api/orders/serve-temp-pdf/?file=${encodeURIComponent(filename)}`;
      setPdfUrl(url);
      console.log('📄 PDF URL generada:', url);
      console.log('📄 Filename extraído:', filename);
    } else {
      setPdfUrl(null);
    }

    // Cleanup
    return () => {
      if (pdfUrl && pdfUrl.startsWith('blob:')) {
        URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [data]);


  const handleSearchQuotes = async () => {
    if (!orderTotal || parseFloat(orderTotal) <= 0) {
      alert('Ingresa un monto válido para buscar propuestas');
      return;
    }
    setSearchingQuotes(true);
    try {
      const res = await ordersAPI.matchQuotes(orderTotal);
      setCurrentQuoteMatch(res.data);
      if (res.data?.recommended) {
        setSelectedQuote({ id: res.data.recommended.id, match_type: res.data.match_type });
      } else {
        setSelectedQuote(null);
      }
    } catch (e) {
      alert('Error buscando propuestas: ' + (e.response?.data?.error || e.message));
    } finally {
      setSearchingQuotes(false);
    }
  };

  const handleQuoteSelect = (quote, matchType) => {
    setSelectedQuote({
      id: quote.id,
      match_type: matchType,
    });
  };

  const handleSubmit = async () => {
    if (!selectedQuote?.id) {
      alert('Debes seleccionar una propuesta para vincular esta orden');
      return;
    }

    if (!orderNumber || orderNumber.trim() === '') {
      alert('Debes ingresar el número de orden de compra');
      return;
    }

    setLoading(true);

    try {
      const pdfPath = data?.temp_files?.pdf_path || '';
      const xmlPath = data?.temp_files?.xml_path || '';

      const confirmData = {
        order_number: orderNumber.trim(),
        order_date: data?.order_date || new Date().toISOString().split('T')[0],
        quote_id: selectedQuote.id,
        subtotal: orderTotal || data?.total || '0.00',
        amount_includes_tax: selectedQuote.match_type === 'with_tax',
        matched_by_amount: ['exact', 'with_tax'].includes(selectedQuote.match_type),
        match_with_tax: selectedQuote.match_type === 'with_tax',
        pdf_file_path: pdfPath,
        xml_file_path: xmlPath,
      };

      await onConfirm(confirmData);
      
    } catch (error) {
      console.error('❌ Error al confirmar orden:', error);
      
      let errorMsg = 'Error al crear orden';
      if (error.response?.data?.error) {
        if (typeof error.response.data.error === 'object') {
          errorMsg = Object.entries(error.response.data.error)
            .map(([key, value]) => `${key}: ${Array.isArray(value) ? value.join(', ') : value}`)
            .join('; ');
        } else {
          errorMsg = error.response.data.error;
        }
      } else if (error.message) {
        errorMsg = error.message;
      }

      alert(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleGoToQuotes = () => {
    toggle();
    window.location.href = '/admin/inbox/quotes';
  };

  if (!data) return null;

  // Usar resultado de búsqueda manual si existe, si no el automático del upload
  const activeQuoteMatch = currentQuoteMatch || quoteMatch;
  const hasExactMatch = activeQuoteMatch?.exact_match || activeQuoteMatch?.match_with_tax;

  const getMatchBadge = (matchType) => {
    const badges = {
      exact: { color: 'success', text: 'Match Exacto', icon: 'ni-check-bold' },
      with_tax: { color: 'info', text: 'Match con IVA', icon: 'ni-money-coins' },
    };
    return badges[matchType] || badges.exact;
  };

  return (
    <Modal 
      isOpen={isOpen} 
      toggle={toggle} 
      size="xl"
      backdrop="static"
      className="modal-dialog-centered"
      style={{ maxWidth: '95vw' }}
    >
      {/* ✅ HEADER MEJORADO */}
      <ModalHeader 
        toggle={toggle}
        className="bg-gradient-primary border-0"
        style={{
          background: 'linear-gradient(135deg, #038D81 0%, #026B62 100%)',
        }}
      >
        <div style={{ position: 'relative', zIndex: 2 }}>
          <h5 className="mb-0 text-white font-weight-bold">
            <i className="ni ni-bag-17 me-2"></i>
            Vincular Orden de Compra con Propuesta
          </h5>
          <small className="text-white opacity-8 d-block mt-1">
            Verifica los datos extraídos y visualiza el PDF simultáneamente
          </small>
        </div>
      </ModalHeader>
      
      {/* ✅ BODY CON LAYOUT SPLIT */}
      <ModalBody className="p-0" style={{ height: '75vh', overflow: 'hidden' }}>
        <Row className="g-0 h-100">
          {/* ========== COLUMNA IZQUIERDA: DATOS (50%) ========== */}
          <Col md="6" className="border-end" style={{ 
            overflowY: 'auto', 
            height: '100%',
            background: '#f8fafc'
          }}>
            <div className="p-4">
              {/* Información de la Orden */}
              <div className="card shadow border-0 mb-3">
                <div className="card-header bg-gradient-primary">
                  <h6 className="mb-0 text-white">
                    <i className="ni ni-bag-17 me-2"></i>
                    Información Extraída del PDF
                  </h6>
                </div>
                <div className="card-body p-3">
                  <div className="row">
                    <div className="col-md-6">
                      <div className="form-group mb-0">
                        <label className="form-control-label text-uppercase text-xs font-weight-bold">
                          Número de Orden <span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control form-control-alternative"
                          value={orderNumber}
                          onChange={(e) => setOrderNumber(e.target.value)}
                          placeholder="Ej: 2300045659"
                        />
                        {!data.order_number && (
                          <small className="text-warning d-block mt-1">
                            <i className="ni ni-bell-55 me-1"></i>
                            No se detectó automáticamente, ingrésalo manualmente
                          </small>
                        )}
                        {data.order_number && (
                          <small className="text-success d-block mt-1">
                            <i className="ni ni-check-bold me-1"></i>
                            Detectado automáticamente (puedes editarlo)
                          </small>
                        )}
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-group mb-0">
                        <label className="form-control-label text-uppercase text-xs font-weight-bold">
                          Monto Total {(!data?.total || parseFloat(data.total) === 0) && <span className="text-danger">*</span>}
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          className={`form-control form-control-alternative font-weight-bold ${
                            (!data?.total || parseFloat(data.total) === 0) ? 'border-warning' : 'bg-secondary'
                          }`}
                          value={orderTotal}
                          onChange={(e) => setOrderTotal(e.target.value)}
                          readOnly={data?.total && parseFloat(data.total) > 0}
                          disabled={data?.total && parseFloat(data.total) > 0}
                          placeholder="Ingresa el monto total"
                        />
                        {(!data?.total || parseFloat(data.total) === 0) ? (
                          <>
                            <small className="text-warning d-block mt-1">
                              <i className="ni ni-bell-55 me-1"></i>
                              PDF sin texto — ingresa el monto y busca la propuesta
                            </small>
                            <Button
                              color="warning"
                              size="sm"
                              className="mt-2"
                              onClick={handleSearchQuotes}
                              disabled={searchingQuotes}
                            >
                              {searchingQuotes
                                ? <><span className="spinner-border spinner-border-sm me-1" />Buscando...</>
                                : <><i className="ni ni-zoom-split-in me-1" />Buscar propuesta</>
                              }
                            </Button>
                          </>
                        ) : (
                          <small className="text-success d-block mt-1">
                            <i className="ni ni-check-bold me-1"></i>
                            Detectado automáticamente
                          </small>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Propuestas Encontradas */}
              {hasExactMatch ? (
                <div className="card shadow border-0 mb-3">
                  <div className="card-header bg-gradient-primary">
                    <h6 className="mb-0 text-white">
                      <i className="ni ni-check-bold me-2"></i>
                      Propuesta Encontrada
                    </h6>
                  </div>
                  <div className="card-body p-3">
                    <p className="text-sm text-muted mb-3">
                      Se encontró una propuesta con el monto exacto (tolerancia ±$1.00):
                    </p>

                    {/* Match Exacto */}
                    {quoteMatch?.exact_match && (
                      <div
                        className={`card shadow-sm mb-2 cursor-pointer ${
                          selectedQuote?.id === activeQuoteMatch.exact_match.id ? 'border-success border-2' : 'border-0'
                        }`}
                        onClick={() => handleQuoteSelect(activeQuoteMatch.exact_match, 'exact')}
                        style={{ cursor: 'pointer', transition: 'all 0.2s ease' }}
                      >
                        <div className="card-body p-3">
                          <div className="d-flex justify-content-between align-items-center">
                            <div className="custom-control custom-radio">
                              <input
                                type="radio"
                                id="quote-exact"
                                name="quoteSelection"
                                className="custom-control-input"
                                checked={selectedQuote?.id === activeQuoteMatch.exact_match.id}
                                onChange={() => handleQuoteSelect(activeQuoteMatch.exact_match, 'exact')}
                              />
                              <label className="custom-control-label" htmlFor="quote-exact">
                                <strong className="text-dark">{activeQuoteMatch.exact_match.proposal_number}</strong>
                                <span className="badge badge-sm bg-gradient-secondary ms-2">
                                  v{activeQuoteMatch.exact_match.version}
                                </span>
                                <br />
                                <small className="text-muted">
                                  Cliente: {activeQuoteMatch.exact_match.client_name}
                                  <br />
                                  Subtotal: ${parseFloat(activeQuoteMatch.exact_match.subtotal).toFixed(2)}
                                </small>
                              </label>
                            </div>
                            <span className={`badge badge-${getMatchBadge('exact').color}`}>
                              <i className={`ni ${getMatchBadge('exact').icon} me-1`}></i>
                              {getMatchBadge('exact').text}
                            </span>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Match con IVA */}
                    {quoteMatch?.match_with_tax && !quoteMatch?.exact_match && (
                      <div
                        className={`card shadow-sm mb-2 cursor-pointer ${
                          selectedQuote?.id === activeQuoteMatch.match_with_tax.id ? 'border-info border-2' : 'border-0'
                        }`}
                        onClick={() => handleQuoteSelect(activeQuoteMatch.match_with_tax, 'with_tax')}
                        style={{ cursor: 'pointer', transition: 'all 0.2s ease' }}
                      >
                        <div className="card-body p-3">
                          <div className="d-flex justify-content-between align-items-center">
                            <div className="custom-control custom-radio">
                              <input
                                type="radio"
                                id="quote-tax"
                                name="quoteSelection"
                                className="custom-control-input"
                                checked={selectedQuote?.id === activeQuoteMatch.match_with_tax.id}
                                onChange={() => handleQuoteSelect(activeQuoteMatch.match_with_tax, 'with_tax')}
                              />
                              <label className="custom-control-label" htmlFor="quote-tax">
                                <strong className="text-dark">{activeQuoteMatch.match_with_tax.proposal_number}</strong>
                                <span className="badge badge-sm bg-gradient-secondary ms-2">
                                  v{activeQuoteMatch.match_with_tax.version}
                                </span>
                                <br />
                                <small className="text-muted">
                                  Cliente: {activeQuoteMatch.match_with_tax.client_name}
                                  <br />
                                  Total con IVA: ${parseFloat(activeQuoteMatch.match_with_tax.total).toFixed(2)}
                                </small>
                              </label>
                            </div>
                            <span className={`badge badge-${getMatchBadge('with_tax').color}`}>
                              <i className={`ni ${getMatchBadge('with_tax').icon} me-1`}></i>
                              {getMatchBadge('with_tax').text}
                            </span>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Comparación de Montos */}
                    {selectedQuote?.id && quoteMatch?.recommended && (
                      <Alert color="bg-gradient-primary" className="mt-3 mb-0">
                        <div className="d-flex align-items-center">
                          <i className="ni ni-chart-bar-32 text-lg me-2"></i>
                          <div>
                            <strong>Comparación de montos:</strong>
                            <br />
                            <small>
                              Cotización: ${parseFloat(activeQuoteMatch.recommended.subtotal).toFixed(2)}
                              {' • '}
                              Orden: ${parseFloat(data.total).toFixed(2)}
                              {' • '}
                              Diferencia: ${Math.abs(parseFloat(activeQuoteMatch.recommended.subtotal) - parseFloat(data.total)).toFixed(2)}
                            </small>
                          </div>
                        </div>
                      </Alert>
                    )}
                  </div>
                </div>
              ) : (
                /* Sin Match Exacto */
                <Alert color="danger" className="shadow-sm">
                  <div className="d-flex align-items-start">
                    <div className="icon icon-shape bg-gradient-danger text-white rounded-circle shadow me-3">
                      <i className="ni ni-fat-remove"></i>
                    </div>
                    <div className="flex-grow-1">
                      <h4 className="alert-heading mb-2">
                        No se encontró propuesta con monto exacto
                      </h4>
                      <p className="mb-2">
                        No existe ninguna propuesta con el monto <strong>${parseFloat(orderTotal || data.total || 0).toFixed(2)}</strong> (tolerancia ±$1.00)
                      </p>

                      {/* Reintentar con monto diferente */}
                      <div className="d-flex align-items-center gap-2 mb-3">
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          className="form-control form-control-sm border-warning"
                          style={{ maxWidth: 160 }}
                          value={orderTotal}
                          onChange={(e) => setOrderTotal(e.target.value)}
                          placeholder="Monto a buscar"
                        />
                        <Button
                          color="warning"
                          size="sm"
                          onClick={handleSearchQuotes}
                          disabled={searchingQuotes}
                        >
                          {searchingQuotes
                            ? <span className="spinner-border spinner-border-sm" />
                            : <><i className="ni ni-zoom-split-in me-1" />Reintentar</>
                          }
                        </Button>
                      </div>

                      <hr className="my-2" />
                      <p className="mb-2">
                        <strong>Otras opciones:</strong>
                      </p>
                      <div className="d-flex gap-2">
                        <Button
                          color="primary"
                          size="sm"
                          onClick={handleGoToQuotes}
                          className="shadow-sm"
                        >
                          <i className="ni ni-cloud-upload-96 me-2"></i>
                          Subir propuesta primero
                        </Button>
                        <Button
                          color="secondary"
                          size="sm"
                          onClick={toggle}
                        >
                          <i className="ni ni-fat-remove me-2"></i>
                          Cancelar
                        </Button>
                      </div>
                    </div>
                  </div>
                </Alert>
              )}
            </div>
          </Col>

          {/* ========== COLUMNA DERECHA: PDF VIEWER (50%) ========== */}
          <Col md="6" className="bg-white" style={{ 
            height: '100%',
            display: 'flex',
            flexDirection: 'column'
          }}>
            {/* Header del PDF */}
            <div 
              className="p-3 border-bottom"
              style={{
                background: 'linear-gradient(to right, #f8fafc, #e8f5f4)',
              }}
            >
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="mb-0 font-weight-bold text-dark">
                    <i className="ni ni-single-copy-04 me-2 text-primary"></i>
                    Vista Previa del PDF
                  </h6>
                  <small className="text-muted">
                    Orden de Compra: {orderNumber || 'Sin número'}
                  </small>
                </div>
                <div className="d-flex gap-2">
                  <Button
                    color="primary"
                    size="sm"
                    className="btn-icon"
                    onClick={() => window.open(pdfUrl, '_blank')}
                    disabled={!pdfUrl}
                    title="Abrir en nueva pestaña"
                  >
                    <span className="btn-inner--icon">
                      <i className="ni ni-laptop"></i>
                    </span>
                  </Button>
                </div>
              </div>
            </div>

            {/* Contenedor del PDF */}
            <div 
              className="flex-grow-1 position-relative"
              style={{ 
                background: '#f1f5f9',
                overflow: 'hidden'
              }}
            >
              {pdfUrl ? (
                <iframe
                  src={pdfUrl}
                  style={{
                    width: '100%',
                    height: '100%',
                    border: 'none',
                  }}
                  title="Vista Previa Orden de Compra"
                />
              ) : (
                <div className="d-flex flex-column align-items-center justify-content-center h-100">
                  <div 
                    className="icon icon-shape bg-gradient-primary shadow-primary text-center rounded-circle mb-3"
                    style={{ width: '80px', height: '80px' }}
                  >
                    <i className="ni ni-folder-17 text-white" style={{ fontSize: '2rem', lineHeight: '80px' }}></i>
                  </div>
                  <h5 className="text-dark">PDF no disponible</h5>
                  <p className="text-sm text-muted">
                    El documento no se pudo cargar
                  </p>
                </div>
              )}
            </div>
          </Col>
        </Row>
      </ModalBody>

      {/* ✅ FOOTER */}
      <ModalFooter className="border-0 bg-light">
        <button 
          className="btn btn-secondary" 
          onClick={toggle} 
          disabled={loading}
        >
          <i className="ni ni-fat-remove me-2"></i>
          Cancelar
        </button>
        <button
          className="btn btn-primary"
          onClick={handleSubmit}
          disabled={loading || !selectedQuote?.id || !orderNumber.trim()}
        >
          {loading ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status"></span>
              Guardando...
            </>
          ) : (
            <>
              <i className="ni ni-check-bold me-2"></i>
              Confirmar y Vincular
            </>
          )}
        </button>
      </ModalFooter>
    </Modal>
  );
};

export default OrderConfirmModal;
